import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalRefreshToken,
} from "@/lib/storeManager";
import { refreshAccessToken } from "@/lib/tokenRefresh";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

function shouldHideApiMessage(url?: string): boolean {
  return hidenMessageAPI.some(path => (url ?? "").includes(path));
}

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId() ?? import.meta.env.VITE_X_TENANTID;

  // Don't add Authorization header for refresh token requests
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      (response.config as { showNotification?: boolean }).showNotification ===
      false
    ) {
      return response;
    }
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config as
      | (typeof error.config & { showNotification?: boolean; _retry?: boolean })
      | undefined;
    const suppressNotification =
      originalRequest?.showNotification === false ||
      shouldHideApiMessage(originalRequest?.url);

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      if (!suppressNotification) {
        notify.error(
          error.response.data.message ??
            error.response.data.error?.message ??
            error.response.data.error
        );
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest!.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest!);
        });
      }

      originalRequest!._retry = true;
      isRefreshing = true;

      try {
        const { access: newAccessToken, refresh: newRefresh } =
          await refreshAccessToken();
        if (newRefresh) {
          setLocalRefreshToken(newRefresh);
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest!.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest!);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      if (!suppressNotification) {
        notify.error(
          error.response?.data?.error?.message ??
            error.response?.data?.message ??
            error.response?.data?.error ??
            "Request failed"
        );
      }
      return Promise.reject(error);
    }
  }
);

export { refreshAccessToken } from "@/lib/tokenRefresh";
